import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CloseSvg from '@/components/common/CloseSvg';
import { useAddItem } from '@/components/shopping_bag/hooks/useCart';
import { TmProduct } from '@/tscapi/model/product';
import { Cookie } from '@/tscapi/utils/cookie';

import { usePwpState } from './hooks/usePwpState';
import { en, fr } from './i18n';
import { QuantitySelector } from './QuantitySelector';

type UpsellItemProps = {
    UpsellProducts: TmProduct[];
    Update: any;
};

export const UpsellItemCard = ({ UpsellProducts, Update }: UpsellItemProps) => {
    const cookie: Cookie = new Cookie();
    const { i18n, t } = useTranslation('addtobag');
    const { savePwpState, modalState } = usePwpState();

    const shouldShowModal = () => {
        if (!modalState?.timestamp) {
            return true; // Show modal if no timestamp exists
        }
        const isStale = Date.now() - modalState.timestamp > 24 * 60 * 60 * 1000; // 24 hours stale time check
        return isStale || !modalState.userPrefersClosed; // Show modal if stale or user doesn't prefer closed
    };

    const handleClose = () => {
        savePwpState({ userPrefersClosed: true });
    };

    useEffect(() => {
        i18n.addResourceBundle('en', 'addtobag', en, true, true);
        i18n.addResourceBundle('fr', 'addtobag', fr, true, true);
    }, [i18n]);

    const [quantity, setQuantity] = useState(1); // Local state for quantity
    const mutation = useAddItem(cookie.UserAccessToken?.Edp, cookie.CartGuid);
    const product = UpsellProducts && UpsellProducts[0];
    const edpData = product?.Edps?.find((x) => x.EdpNo === product?.DefaultEdp);
    const message = product?.ProductMessages?.UpsellMessage?.trim();
    const isUpsellUnlocked = Boolean(message && message.toLowerCase().startsWith('deal'));
    const isRegularUpsell = Boolean(message && message.toLowerCase().startsWith('you'));

    const handleQuantityChange = (newQuantity: number) => {
        setQuantity(newQuantity); // Update local state
    };

    const handleAddToBag = () => {
        if (!product || !edpData) return;
        mutation.mutate(
            { itemEDP: edpData.EdpNo, Quantity: quantity },
            {
                onSuccess: (data) => {
                    window.analyticsData.event.eventTitle = 'add-to-cart';
                },
            },
        );

        // Perform the API call
    };

    if (!product || !edpData || !shouldShowModal()) {
        return null;
    }

    return (
        <div className="border-b-0 border-l-0 border-r-0 border-t-2 border-solid border-[#e3e3e3] pt-5">
            <div className="relative border-2 border-solid p-2.5" style={{ borderColor: '#2a979d' }}>
                <button className="absolute right-2.5 top-2.5 cursor-pointer focus:outline-none" onClick={handleClose}>
                    <span className="visually-hidden">{t('close')}</span>
                    <CloseSvg />
                </button>
                {product?.ProductMessages?.UpsellMessage && (
                    <div className="mb-2.5 inline-flex items-center pr-4">
                        {isUpsellUnlocked && !isRegularUpsell && (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 15 15"
                                width="15"
                                height="15"
                                className="mr-1.5 inline-block"
                                fill="#01b9bb"
                            >
                                <path d="M14.52 8.27 7.55 1.3a.543.543 0 0 0-.34-.16L1.73.64C1.11.58.59 1.1.64 1.73l.5 5.48c.01.13.07.25.16.34l6.97 6.97c.22.22.57.22.78 0l5.47-5.47c.22-.22.22-.57 0-.78ZM3.81 3.81c-.44.44-1.15.44-1.59 0-.44-.44-.44-1.15 0-1.59.44-.44 1.15-.44 1.59 0s.44 1.15 0 1.59Z" />
                            </svg>
                        )}

                        <span className="text-xsm font-semibold leading-4.5">
                            {product.ProductMessages.UpsellMessage}
                        </span>
                    </div>
                )}
                <div className="grid grid-cols-[95px_auto] grid-rows-[auto_auto_auto] gap-x-[15px] md:grid-cols-[100px_auto_200px] md3:grid-cols-[100px_auto_25px] min-[1160px]:grid-cols-[100px_auto_200px]">
                    <div className="col-start-1 col-end-2 row-start-1 row-end-4">
                        <a href={product.ProductLink.LinkWithoutDomain}>
                            <img
                                className="img-responsive"
                                width="100"
                                height="100"
                                alt={product.Name}
                                src={`https://item.tscimg.ca/TSC/${edpData.ImageUrl}?impolicy=S`}
                            />
                        </a>
                    </div>
                    <div className="col-start-2 col-end-3 row-start-1 row-end-2">
                        {product.PriceIsLabel && (
                            <div className="mb-2.5 text-xxsm font-extrabold uppercase leading-[14px]">
                                {product.PriceIsLabel}
                            </div>
                        )}
                        <a href={product.ProductLink.LinkWithoutDomain}>
                            <h3 className="m-0 mb-2.5 line-clamp-2 text-xsm font-semibold leading-4.5">
                                {product.Name}
                            </h3>
                        </a>

                        {edpData.Inventory < 10 && edpData.Inventory > 0 && (
                            <div className="text-xxs font-semibold leading-4.5 tracking-wide">
                                Only {edpData.Inventory} left!
                            </div>
                        )}
                    </div>
                    {(isUpsellUnlocked || isRegularUpsell) && (
                        <div className="col-start-2 col-end-3 row-start-2 row-end-3 mb-2.5 mt-2.5 flex justify-between sm:col-end-3 sm:row-start-2 sm:row-end-3 sm:items-end sm:justify-start lg:col-end-4 lg:row-end-4 lg:mb-2.5 lg:mt-2.5 lg:justify-between min-[1160px]:row-start-2 min-[1160px]:row-end-3 xl:col-end-3 xl:items-end xl:justify-start">
                            <QuantitySelector
                                value={quantity}
                                lineId={0}
                                inventory={edpData.Inventory}
                                edpNo={edpData.EdpNo}
                                update={{ mutate: ({ qty }) => handleQuantityChange(qty) }} // Custom update function
                            />
                        </div>
                    )}

                    <div className="col-start-2 col-end-3 row-start-3 row-end-4 flex justify-end text-right md:col-start-3 md:col-end-4 md:row-start-1 md:row-end-4 md3:col-start-2 md3:col-end-4 md3:row-start-3 md3:row-end-4 min-[1160px]:col-start-3 min-[1160px]:col-end-4 min-[1160px]:row-start-1 min-[1160px]:row-end-5">
                        <div className="w-full">
                            <div style={{ textAlign: 'right' }}>
                                <span className="text-sm font-semibold">{edpData.AppliedPrice}</span>
                                {edpData.AppliedPrice !== edpData.WasPrice && (
                                    <span className="ml-2.5 text-sm font-normal line-through">{edpData.WasPrice}</span>
                                )}
                            </div>
                            {(isUpsellUnlocked || isRegularUpsell) && (
                                <button
                                    type="button"
                                    aria-label={t('Add to Bag')}
                                    className="mx-auto my-0 mt-3 inline w-full max-w-44 cursor-pointer select-none whitespace-nowrap border border-solid border-black bg-black px-3 py-3.5 text-center text-sm font-semibold text-white"
                                    onClick={() => handleAddToBag()}
                                >
                                    {t('Add to Bag')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
